import { useRef } from 'react';

/**
 * A `<Document>` key that changes whenever the pdf source changes.
 *
 * react-pdf reloads a mounted `<Document>` in place when `file` changes, and its
 * `loadDocument` cleanup destroys the old pdf.js loading task without guarding
 * the resolve path — only the reject path checks `loadingTask.destroyed`. So a
 * `file` swap mid-load lands the already-destroyed PDFDocumentProxy in state, and
 * the `<Page>` children then call `getPage()` on a transport whose messageHandler
 * is null: "Cannot read properties of null (reading 'sendWithPromise')" (DC-1069).
 * Keying on the source instead of just the name remounts, keeping that proxy out
 * of state — a refetched blob under an unchanged file name is the common trigger.
 *
 * Every mounted `<Document>` whose `file` can change needs this, not just the
 * viewer the crash was first reported against.
 */
export const usePdfDocumentKey = (file: Blob | string | undefined, fileName?: string): string => {
	const source = useRef({ file, fileName });
	const generation = useRef(0);

	if (!Object.is(source.current.file, file) || source.current.fileName !== fileName) {
		source.current = { file, fileName };
		generation.current += 1;
	}

	return `${fileName ?? 'document'}-${generation.current}`;
};
